



















































import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import { mapMutations, mapActions, mapGetters } from 'vuex'
import { RouteNames } from '@/router'
import SchemeCommon from '@/abstract/SchemeCommon'
import Icon from '@/components/Icon/Icon.vue'
import OptionLink from '@/components/SchemeCard/OptionLink.vue'
import CardOptionBadge from '@/components/CardOptionBadge/CardOptionBadge.vue'
import { SCRIPTS__REMOVE_OPTION } from '@/store/actions'
import { _SCHEME__COLLAPSE_OPTION } from '@/store/mutations'
import { ScriptOptionRemovePayload } from '@/store/modules/scripts/actions'
import { AppColor } from '@/@types'
import { CardOptionType } from '@/constants/scripts'

import type { RawLocation } from 'vue-router'
import type { AxiosPromise } from 'axios'
import type { LinkCoords } from '@/components/SchemeCard/OptionLink.vue'
import type { SchemeCollapseOptionMutationPayload } from '@/store/modules/scripts/mutations'
import type { Point } from '@/@types'
import type { CardOption } from '@/models/scripts'

interface Data {
  coords: LinkCoords
}

interface Methods {
  [SCRIPTS__REMOVE_OPTION]: (
    payload: ScriptOptionRemovePayload,
  ) => AxiosPromise<boolean>
  [_SCHEME__COLLAPSE_OPTION]: (
    payload: SchemeCollapseOptionMutationPayload,
  ) => void
  computeLinkCoords: () => void
  toggle: () => void
  remove: () => void
  scrollToCard: () => void
}

interface Computed {
  getSchemeUpdate: number
  isLeadToCard: boolean
  toggleClasses: string
  eyeIconClass: string
  optionClasses: Record<string, boolean>
  toggleTooltipText: string
  bgColor: Record<string, string>
  borderColor: Record<string, string>
  color: Record<string, string>
  linkTarget: '_blank' | '_current'
  linkRoute: RawLocation | null
  linkComponent: string
  editRoute: RawLocation
}

interface Props {
  zoom: number
  isEdit: boolean
  cardId: string
  scriptId: string
  instanceId?: string
  current: string
  option: CardOption
}

const INITIAL_LINK_COORDS: LinkCoords = {
  to: {
    x: 0,
    y: 0,
  },
  from: {
    x: 0,
    y: 0,
  },
  zoom: 1,
}

export default SchemeCommon.extend<Data, Methods, Computed, Props>({
  components: {
    Icon,
    OptionLink,
    CardOptionBadge,
  },
  props: {
    zoom: {
      required: true,
    },
    isEdit: {
      required: true,
    },
    cardId: {
      required: true,
    },
    scriptId: {
      required: true,
    },
    instanceId: {
      default: () => undefined,
    },
    current: {
      required: true,
    },
    option: {
      required: true,
    },
  },
  data() {
    return {
      coords: { ...INITIAL_LINK_COORDS },
    }
  },
  computed: {
    ...mapGetters(['getSchemeUpdate']),
    isLeadToCard(): boolean {
      return this.option.type === CardOptionType.Card
    },
    toggleClasses(): string {
      return this.option.type === CardOptionType.Card && !!this.option.collapsed
        ? '-collapsed'
        : ''
    },
    eyeIconClass(): string {
      return this.option.type === CardOptionType.Card && !this.option.collapsed
        ? 'zmdi-eye'
        : 'zmdi-eye-off'
    },
    optionClasses(): Record<string, boolean> {
      const option = `-option-${this.option.n}`
      const id = `-oid-${this.option.id}`
      return {
        [option]: true,
        [id]: true,
      }
    },
    toggleTooltipText(): string {
      return this.option.type === CardOptionType.Card && this.option.collapsed
        ? this.$tc('common.expand')
        : this.$tc('common.collapse')
    },
    bgColor(): Record<string, string> {
      let backgroundColor = `#${this.option.color}`
      if (this.option.type === CardOptionType.Final && this.option.t) {
        backgroundColor = `#${AppColor.Green}`
      }
      return { backgroundColor }
    },
    borderColor(): Record<string, string> {
      let borderColor = `#${this.option.color}`
      if (this.option.type === CardOptionType.Final && this.option.t) {
        borderColor = `#${AppColor.Green}`
      }
      return { borderColor }
    },
    color(): Record<string, string> {
      let color = `#${this.option.color}`
      if (this.option.type === CardOptionType.Final && this.option.t) {
        color = `#${AppColor.Green}`
      }
      return { color }
    },
    linkTarget(): '_blank' | '_current' {
      if (this.option.type === CardOptionType.Script) {
        return '_blank'
      }
      return '_current'
    },
    linkRoute(): RawLocation | null {
      const { isEdit, scriptId, instanceId } = this

      if (this.option.type !== CardOptionType.Card || !instanceId) {
        return null
      }

      return isEdit ? {
        name: RouteNames.ScriptPage,
        params: { scriptId, instanceId: instanceId },
        query: { startCardNum: `${this.option.link}` },
      } : {
        name: RouteNames.ScriptEdit,
        params: {
          cardId: this.option.nextCardId,
          scriptId,
          instanceId: instanceId,
        },
      }
    },
    linkComponent(): string {
      // Temporally feature disabled (Task - 136)
      // return this.linkRoute ? 'router-link' : 'span'
      return 'span'
    },
    editRoute(): RawLocation {
      return {
        name: RouteNames.ScriptOption,
        params: {
          cardId: this.cardId,
          scriptId: this.scriptId,
          instanceId: this.instanceId as string,
          variant: this.current,
          optionId: this.option.id,
        },
      }
    },
  },
  watch: {
    getSchemeUpdate() {
      this.$nextTick().then(() => {
        this.computeLinkCoords()
      })
    },
    option: {
      handler() {
        this.$nextTick().then(() => {
          this.computeLinkCoords()
        })
      },
      deep: true,
    },
  },
  mounted() {
    this.computeLinkCoords()
  },
  methods: {
    ...mapActions([SCRIPTS__REMOVE_OPTION]),
    ...mapMutations([_SCHEME__COLLAPSE_OPTION]),
    computeLinkCoords() {
      const me = this.$refs.me as Element
      const myRect = me.getBoundingClientRect()
      this.coords.zoom = this.zoom
      this.coords.from = {
        x: myRect.right,
        y: myRect.top + myRect.height / 2,
      } as Point

      let toX = this.coords.from.x + 25
      let toY = this.coords.from.y

      const target = document.querySelector(`#card-num-${this.option.link}`)
      if (
        target &&
        this.option.type === CardOptionType.Card &&
        !this.option.isLink
      ) {
        const targetRect = target.getBoundingClientRect()
        toX = targetRect.left
        toY = targetRect.top + targetRect.height / 2
      }

      this.coords.to = {
        x: toX,
        y: toY,
      }
    },
    toggle() {
      if (!this.option.id) {
        return
      }

      this[_SCHEME__COLLAPSE_OPTION]({
        cardId: this.cardId,
        optionId: this.option.id,
      })
      this.updateScheme()
    },
    remove() {
      if (!this.option.id) {
        return
      }

      this.$useConfirmDialog([this.$tc('transition.actions.removeConfirm'), {
          type: 'danger',
          okText: this.$tc('common.remove'),
          cancelText: this.$tc('common.cancel'),
        }], () => {
        const { scriptId } = this.$route.params
        this[SCRIPTS__REMOVE_OPTION]({
          id: scriptId,
          cardId: this.cardId,
          instanceId: this.instanceId as string,
          optionId: this.option.id,
        }).then(() => {
          const text = this.$tc('transition.actions.removeSuccess')
          this.$store.dispatch(ADD_TOAST_MESSAGE, {
            text,
            type: 'info',
          })
          this.updateScheme()
        })
      })
    },
    scrollToCard() {
      if (this.option.link) {
        this.onScrollToCard(this.option.link)
      }
    },
  },
})
